import React from "react";
import { Blobatar } from "@blobatar/react";
import classNames from "../utils/classnames";
import { AvatarProps, AvatarSize } from "./types";

export { AvatarList } from "./AvatarList";
export { AvatarWithName } from "./AvatarWithName";
export { ContributorAvatar, PlaceholderAvatar } from "./ContributorAvatar";
export type { AvatarPerson, AvatarProps, ContributorAvatarProps, PlaceholderAvatarSize } from "./types";

function calculateSize(size: AvatarSize): number {
  if (size.constructor.name === "Number") {
    return size as number;
  }

  switch (size) {
    case "tiny":
      return 20;
    case "small":
      return 32;
    case "normal":
      return 32;
    case "large":
      return 40;
    case "xlarge":
      return 56;
    case "xxlarge":
      return 96;
  }

  throw new Error("Invalid size");
}

//
// Default avatar when a person has no uploaded picture.
//
// We use blobatar (https://blobatar.dev): a deterministic geometric face
// derived from the person's stable handle, so every user "gets a face" and
// the same person always renders the same avatar across the sidebar, threads
// and check-ins. Static (no `animate`) renders a lightweight background-image
// span — cheap even for long avatar lists.
//
function BlobatarAvatar({ person, size, className, testId }: AvatarProps): JSX.Element {
  const around = classNames(
    "rounded-full overflow-hidden shrink-0 border border-stroke-base inline-block flex items-center justify-center",
    className,
  );
  const sizeNumber = calculateSize(size);
  const style = { width: `${sizeNumber}px`, height: `${sizeNumber}px` };
  // Deterministic seed: prefer stable person id, fall back to name.
  const seed = person.id || person.fullName || "?";

  // Map the seed to a hue while skipping the brown sector of the wheel
  // (~20-75°, where oklch ramps land as browns/oranges/olives). The same
  // seed always maps to the same hue, so avatars stay stable across renders.
  const hue = hueFromSeed(seed, 20, 75);

  return (
    <div title={person.fullName ?? ""} className={around} style={style} data-test-id={testId}>
      <Blobatar name={seed} size={sizeNumber} hue={hue} />
    </div>
  );
}

// FNV-1a style hash → uniform 0..1, then remap onto [0,360) minus [gapStart,gapEnd).
// Sector width is preserved; hues below the gap are untouched, hues above shift up.
function hueFromSeed(seed: string, gapStart = 20, gapEnd = 75): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const u = (h >>> 0) / 4294967296; // 0..1
  const usable = 360 - (gapEnd - gapStart);
  const angle = u * usable;
  return angle < gapStart ? angle : angle + (gapEnd - gapStart);
}

//
// Note about "no-referrer":
//
// I noticed an issue with referrer-policy: origin (default) and images from
// googleusercontent.com. Sometimes, not always, the request will be blocked
// with a status 403 code, resulting in rendering the fallback instead of the
// requested image.
//
// Fixed based on this issue: https://github.com/chakra-ui/chakra-ui/issues/5909.
//
function ImageAvatar({ person, size, className, testId }: AvatarProps): JSX.Element {
  if (!person) return <></>;

  const cn = classNames(
    "rounded-full overflow-hidden bg-white shrink-0 border border-stroke-base inline-block",
    className,
  );

  const sizeNumber = calculateSize(size);
  const style = { width: `${sizeNumber}px`, height: `${sizeNumber}px` };

  return (
    <div title={person.fullName ?? ""} className={cn} style={style} data-test-id={testId}>
      <img
        src={person.avatarUrl!}
        alt={person.fullName ?? ""}
        referrerPolicy="no-referrer"
        style={{
          height: "100%",
          width: "100%",
          display: "block",
          maxHeight: size + "px",
        }}
      />
    </div>
  );
}

export function Avatar(props: AvatarProps) {
  if (props.person) {
    if (props.person.avatarUrl) {
      return ImageAvatar(props);
    } else {
      return BlobatarAvatar(props);
    }
  } else {
    return BlobatarAvatar({ person: { fullName: "?" }, size: props.size });
  }
}
